import type { ListBuilderConfiguration } from '../types';

/**
 * Item Client History list configuration
 * Shows client purchase history for a specific item
 * Features custom chart component for trend visualization
 * Converted from JSON to TypeScript for type safety
 */
export const ITEM_CLIENT_HISTORY_CONFIG: ListBuilderConfiguration = {
	filters: {
		activeFilters: ['clientHistoryKeywords', 'salespeople', 'itemClientTrend'],
		defaultFilters: {},
	},
	lists: [
		{
			id: 'default-client-history-all',
			cacheTTL: 600000,
			name: 'All Client History',
			type: 'default',
			active: true,
			searchEnabled: true,
			searchPlaceholder: 'Search clients...',
			// This tab shows invoice-derived purchase history, so the
			// salespeople filter is locked the same way the invoice list
			// locks invoiceSalesRep (see BASE_FILTER_RULES in
			// libs/core/.../invoice/shared.ts): users with invoices:view:any
			// filter freely; everyone else is locked to Me and the SQL is
			// further restricted by the service-level RBAC predicate to
			// primary OR alternate sales rep.
			filterRules: {
				salespeople: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'invoices:view:any', negated: true },
						],
					},
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'invoices:view:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'invoices:view:any', negated: true },
						],
					},
				},
			},
			columns: [
				{
					id: 'client',
					fields: [
						{
							id: 'client-name-field',
							link: {
								type: 'object',
								enabled: true,
								idField: 'clientId',
								objectTable: 'client',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'clientName', objectTable: 'client' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'static',
									staticColor: { text: '#3b82f6' },
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'address-line-1-field',
							dataConfig: {
								type: 'template',
								template: '{address1} {address2}',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'address-line-2-field',
							dataConfig: {
								type: 'template',
								template: '{city}, {state} {zip}',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'Client' }],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'salesperson',
					fields: [
						{
							id: 'salesperson-name-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'salespersonName' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'salesperson-header', text: 'Salesperson' }],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'lastPurchase',
					fields: [
						{
							id: 'last-purchase-total-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchaseTotal' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'line-break-4',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'last-purchase-quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchaseQuantity' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-5',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'last-purchase-price-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'lastPurchasePrice' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'last-purchase-header-1', text: 'Last Purchase $' },
						{ id: 'last-purchase-header-line-break-1', text: '' },
						{ id: 'last-purchase-header-2', text: 'Qty' },
						{ id: 'last-purchase-header-line-break-2', text: '' },
						{ id: 'last-purchase-header-3', text: 'Per' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'quantities',
					width: 150,
					fields: [
						{
							id: 'total-quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-6',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'max-quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'maxQuantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 0 },
							},
							displayStyle: 'number',
						},
						{
							id: 'line-break-7',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'avg-quantity-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'avgQuantityLast12Months' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { decimalPlaces: 1 },
							},
							displayStyle: 'number',
						},
					],
					headers: [
						{ id: 'h4', text: '12-Month Total' },
						{ id: 'h4-line-break-1', text: '' },
						{ id: 'h5', text: '12-Month Max' },
						{ id: 'h5-line-break-2', text: '' },
						{ id: 'h6', text: '12-Month Avg' },
					],
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'fixed', preset: 'small' },
					verticalAlignment: 'middle',
				},
				{
					id: 'chart',
					fields: [
						{
							id: 'chart-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'monthlyData' },
								defaultValue: '[]',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'custom',
							customComponent: { id: 'item-client-history-trend-chart' },
						},
					],
					headers: [{ id: 'h4', text: '12 Month Trend' }],
					minWidth: 200,
					sortable: false,
					alignment: 'left',
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	objects: [
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [
				{ fieldName: 'clientId', fieldLabel: 'ID' },
				{ fieldName: 'clientName', fieldLabel: 'Name' },
			],
		},
		{
			baseUrl: '/invoices/:id',
			objectLabel: 'Invoice',
			objectTable: 'invoice',
			selectedFields: [
				{ fieldName: 'lastInvoiceId', fieldLabel: 'ID' },
				{ fieldName: 'lastInvoiceExternalId', fieldLabel: 'Invoice #' },
			],
		},
	],
};
